// 182 B.3 — THE PROSPECT FUNNEL, the Reports view's third stand-alone panel
// (beside PermissionsReportPanel): a from/to pair defaulting to the current
// year, the rows per lead source with a totals row, and `lostBy` as a second
// small table under it. Numbers only; no chart. The arithmetic is the server's
// (`GET /api/reports/funnel` → lib/reportsAggregates.computeFunnel, pure and
// frozen in recordings/182-funnel); this panel asks for a window and draws
// what comes back.
//
// THE COHORT IS BY CREATION DATE, and the screen says so: the conversion date
// is not stored, so "converted" is every client of the cohort whose prospect
// switch is off — including one created directly as a client.
//
// A stable, module-scope component (the ClientCasesCard rule): its state and
// its effect live here, not in ReportsView's render body.

import React, { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { getFunnelReport } from "../api";
import type { FunnelReport, FunnelRow } from "../api";
import type { Translate } from "../lib/interestRefusal";
import DateField from "./DateField";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import type { TaxList } from "../lib/taxonomyTypes";
import { FS } from "../lib/tokens";
import Table from "./Table";

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

const th: React.CSSProperties = { textAlign: "left", padding: "4px 8px", fontSize: FS.s10, color: "var(--tm)", borderBottom: "1px solid var(--br)" };
const thNum: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "4px 8px", fontSize: FS.s11, borderBottom: "1px solid var(--br)" };
const tdNum: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default function FunnelReportPanel() {
  const { t } = useTranslation() as { t: Translate };
  const year = String(new Date().getFullYear());
  const [from, setFrom] = useState(year + "-01-01");
  const [to, setTo] = useState(year + "-12-31");
  const [report, setReport] = useState<FunnelReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const valid = DAY_RE.test(from) && DAY_RE.test(to) && from <= to;
  // Derived, never set inside the effect: the panel is loading while the
  // report it holds is not the one for the window it shows.
  const windowKey = (r: FunnelReport | null) => (r ? r.from + "|" + r.to : "");
  const loading = valid && !error && windowKey(report) !== from + "|" + to;
  const sourceEntries = useTaxonomy("lead_source").entries as TaxList;
  const reasonEntries = useTaxonomy("lost_reason").entries as TaxList;

  useEffect(() => {
    if (!DAY_RE.test(from) || !DAY_RE.test(to) || from > to) return undefined;
    let alive = true;
    getFunnelReport(from, to)
      .then((r) => {
        if (!alive) return;
        setReport(r);
        setError(null);
      })
      .catch((e: unknown) => {
        if (alive) setError(errText(e));
      });
    return () => {
      alive = false;
    };
  }, [from, to]);

  const label = (entries: TaxList, code: string, fallbackKey: string, fallback: string) => {
    if (code === "unknown") return t(fallbackKey, { fallback });
    const resolved: unknown = resolveTaxonomyLabel(code, entries);
    return typeof resolved === "string" && resolved ? resolved : code;
  };
  const sourceLabel = (code: string) => label(sourceEntries, code, "reports.funnel.unknown", "unknown");
  const reasonLabel = (code: string) => label(reasonEntries, code, "reports.funnel.unknown", "unknown");
  const pct = (n: number) => n.toFixed(2) + " %";

  const rows: FunnelRow[] = report ? report.rows : [];
  const totals = report ? report.totals : null;
  const lostBy: [string, number][] = totals
    ? Object.entries(totals.lostBy).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    : [];

  return (
    <div className="funnelReport">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s10, color: "var(--tm)" }}>
          {t("reports.funnel.from", { fallback: "From" })}
          <DateField value={from} onChange={setFrom} style={{ fontSize: FS.s11 }} />
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s10, color: "var(--tm)" }}>
          {t("reports.funnel.to", { fallback: "To" })}
          <DateField value={to} onChange={setTo} style={{ fontSize: FS.s11 }} />
        </label>
        {loading && <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>…</span>}
      </div>
      <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: "0 0 10px" }}>
        {t("reports.funnel.cohort_note", {
          fallback:
            "Cohort by creation date: the conversion date is not stored, so a client created directly as a client counts as converted.",
        })}
      </p>
      {error && (
        <div role="alert" style={{ fontSize: FS.s10, color: "var(--rd)", margin: "4px 0" }}>
          {error}
        </div>
      )}
      {report && rows.length === 0 && !error && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("reports.funnel.empty", { fallback: "No prospects created in this window." })}
        </p>
      )}
      {rows.length > 0 && totals && (
        <div style={{ overflowX: "auto" }}>
          <Table style={{ borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                <th style={th}>{t("reports.funnel.source", { fallback: "Source" })}</th>
                <th style={thNum}>{t("reports.funnel.created", { fallback: "Created" })}</th>
                <th style={thNum}>{t("reports.funnel.open", { fallback: "Open" })}</th>
                <th style={thNum}>{t("reports.funnel.converted", { fallback: "Converted" })}</th>
                <th style={thNum}>{t("reports.funnel.lost", { fallback: "Lost" })}</th>
                <th style={thNum}>{t("reports.funnel.rate", { fallback: "Conversion rate" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.source}>
                  <td style={td}>{sourceLabel(r.source)}</td>
                  <td style={tdNum}>{r.created}</td>
                  <td style={tdNum}>{r.open}</td>
                  <td style={tdNum}>{r.converted}</td>
                  <td style={tdNum}>{r.lost}</td>
                  <td style={tdNum}>{pct(r.conversionRate)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td style={td}>{t("reports.funnel.total", { fallback: "Total" })}</td>
                <td style={tdNum}>{totals.created}</td>
                <td style={tdNum}>{totals.open}</td>
                <td style={tdNum}>{totals.converted}</td>
                <td style={tdNum}>{totals.lost}</td>
                <td style={tdNum}>{pct(totals.conversionRate)}</td>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
      {lostBy.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h4 style={{ fontSize: FS.s11, margin: "0 0 4px" }}>
            {t("reports.funnel.lost_by", { fallback: "Lost — by reason" })}
          </h4>
          <Table style={{ borderCollapse: "collapse", minWidth: 260 }}>
            <thead>
              <tr>
                <th style={th}>{t("reports.funnel.reason", { fallback: "Reason" })}</th>
                <th style={thNum}>{t("reports.funnel.lost", { fallback: "Lost" })}</th>
              </tr>
            </thead>
            <tbody>
              {lostBy.map(([reason, n]) => (
                <tr key={reason}>
                  <td style={td}>{reasonLabel(reason)}</td>
                  <td style={tdNum}>{n}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
